/* eslint-disable @typescript-eslint/no-explicit-any */
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';

import { Checkbox, CheckboxField } from '@/components/ui/catalyst/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useAssignCaseManagerMutation,
  useChangeCaseManagerMutation,
  useGetFirmCaseManagersQuery,
  useRemoveCaseManagerMutation,
  useUpdateCaseManagerConfigMutation,
} from '@/services/api/caseManagerService';

import { usePatchMatterMutation } from '../../services/api';
import { useCreateStaffNoteMutation } from '../../services/api/staffNotesService';
import { captureError } from '../../services/monitoring/sentry';
import {
  BaseMatter,
  MatterStatus,
  MatterType,
  SimplifiedBillingCode,
} from '../../services/types/client-intake-types';
import { extractAttorneyId } from '../../utils/attorney-helpers';
import { isTerminalMatterStatus } from '../matters/config/matterStatusConfig';
import { AdditionalDetailsSection } from './AdditionalDetailsSection';
import { BasicsSection } from './BasicsSection';
import { BillingSection } from './BillingSection';
import { ClientContactSection } from './ClientContactSection';
import { CMSIntegrationSection } from './CMSIntegrationSection';
import { FirmSection } from './FirmSection';
import { FormActions } from './FormActions';
import { FormSection } from './FormSection';
import { MatterStatusNoteModal } from './MatterStatusNoteModal';
import { UpdateMatterFormValues } from './types';

// Schema for form validation
const updateMatterSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Matter name is required'),
  matter_type: z.nativeEnum(MatterType),
  status: z.nativeEnum(MatterStatus),
  date_of_loss: z.string().optional(),
  statute_of_limitations_date: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  firm: z.object({
    id: z.string(),
    name: z.string(),
  }),
  contact_client_directly: z.boolean().optional(),
  representing_attorney: z.string().uuid().nullable().optional(),
  external_cms_url: z.string().url().nullish(),
  external_drive_url: z.string().url().nullish(),
  cms_email: z.string().email().nullish(),
  case_id: z.string().nullish(),
  case_manager_id:z.string().uuid().nullable().optional(),
  auto_task_assignment: z.boolean().optional(),
  simplified_billing_code: z
    .nativeEnum(SimplifiedBillingCode)
    .nullable()
    .optional(),
});

interface UpdateMatterFormProps {
  matterData: BaseMatter;
  onSuccess?: (matterId?: string) => void;
  onCancel?: () => void;
}

export const UpdateMatterForm: React.FC<UpdateMatterFormProps> = ({
  matterData,
  onSuccess,
  onCancel,
}) => {
  const [patchMatter, { isLoading }] = usePatchMatterMutation();
  const [createStaffNote] = useCreateStaffNoteMutation();
  const navigate = useNavigate();
  const [selectedFirm, setSelectedFirm] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [assignCaseManager, { isLoading: isAssigningCaseManager }] =
    useAssignCaseManagerMutation();
  const [changeCaseManager, { isLoading: isChangingCaseManager }] =
    useChangeCaseManagerMutation();
  const [removeCaseManager, { isLoading: isRemovingCaseManager }] =
    useRemoveCaseManagerMutation();
  const [updateCaseManagerConfig, { isLoading: isUpdatingConfig }] =
    useUpdateCaseManagerConfigMutation();

  // State for terminal status note modal
  const [statusNoteModalOpen, setStatusNoteModalOpen] = useState(false);
  const [pendingFormData, setPendingFormData] =
    useState<UpdateMatterFormValues | null>(null);

  const firmId = selectedFirm?.id || matterData?.firm?.id;

  const { data: caseManagers = [] } = useGetFirmCaseManagersQuery(
    { firmId: firmId ?? '' },
    { skip: !firmId },
  );
  const hasCaseManagers = caseManagers.length > 0;
  const hasExistingCaseManager = Boolean(matterData?.case_manager?.id);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<UpdateMatterFormValues>({
    resolver: zodResolver<UpdateMatterFormValues>(updateMatterSchema),
    mode: 'onChange',
    defaultValues: {
      id: matterData?.id || '',
      name: matterData?.name || '',
      matter_type: matterData?.matter_type || MatterType.UNKNOWN,
      status:
        (matterData?.status as MatterStatus) || MatterStatus.INITIAL_REVIEW,
      date_of_loss: matterData?.date_of_loss || '',
      statute_of_limitations_date:
        matterData?.statute_of_limitations_date || '',
      city: matterData?.city || '',
      state: matterData?.state || '',
      firm: {
        id: matterData?.firm?.id || '',
        name: matterData?.firm?.name || '',
      },
      contact_client_directly: matterData?.contact_client_directly ?? true,
      representing_attorney: extractAttorneyId(
        (matterData as any)?.representing_attorney,
      ),
      external_cms_url: matterData?.external_cms_url || null,
      external_drive_url: matterData?.external_drive_url || null,
      cms_email: matterData?.cms_email || null,
      case_id: matterData?.case_id || null,
      simplified_billing_code: matterData?.simplified_billing_code || null,
      case_manager_id: matterData?.case_manager?.id ?? null,
      auto_task_assignment:
        (matterData?.case_manager as { auto_task_assignment?: boolean } | null)
          ?.auto_task_assignment ?? true,
    },
  });

  // Watch billing code field
  const selectedBillingCode= watch('simplified_billing_code');
  const selectedCaseManagerId = watch('case_manager_id') ?? null;

  // Update form values when matterData changes
  useEffect(() => {
    if (matterData) {
      // Set the selected firm state
      setSelectedFirm({
        id: matterData.firm?.id || '',
        name: matterData.firm?.name || '',
      });

      // Reset form with matter data
      reset({
        id: matterData.id,
        name: matterData.name,
        matter_type: matterData.matter_type || MatterType.UNKNOWN,
        status:
          (matterData.status as MatterStatus) || MatterStatus.INITIAL_REVIEW,
        date_of_loss: matterData.date_of_loss || '',
        statute_of_limitations_date:
          matterData.statute_of_limitations_date || '',
        city: matterData.city || '',
        state: matterData.state || '',
        firm: {
          id: matterData.firm?.id || '',
          name: matterData.firm?.name || '',
        },
        contact_client_directly: matterData.contact_client_directly ?? true,
        representing_attorney: extractAttorneyId(
          (matterData as any)?.representing_attorney,
        ),
        external_cms_url: matterData.external_cms_url || null,
        external_drive_url: matterData.external_drive_url || null,
        cms_email: matterData.cms_email || null,
        case_id: matterData.case_id || null,
        simplified_billing_code: matterData.simplified_billing_code || null,
        case_manager_id: matterData?.case_manager?.id ?? null,
        auto_task_assignment:
          (
            matterData?.case_manager as {
              auto_task_assignment?: boolean;
            } | null
          )?.auto_task_assignment ?? true,
      });
    }
  }, [matterData, reset]);

  // Check if status is changing to a terminal status
  const isChangingToTerminalStatus = (newStatus: MatterStatus): boolean => {
    const originalStatus = matterData?.status as MatterStatus;
    return isTerminalMatterStatus(newStatus) && newStatus !== originalStatus;
  };

  // Core submit logic - actually performs the update
  const performSubmit = async (data: UpdateMatterFormValues) => {
    const existingCaseManagerIdAtSubmit = matterData?.case_manager?.id ?? null;
    const nextCaseManagerId = data.case_manager_id ?? null;
    const didCaseManagerChange =
      existingCaseManagerIdAtSubmit !== nextCaseManagerId;

    // Get the original auto_task_assignment value from matter data
    const originalAutoTaskAssignment =
      (matterData?.case_manager as { auto_task_assignment?: boolean } | null)
        ?.auto_task_assignment ?? true;
    const didAutoTaskAssignmentChange =
      data.auto_task_assignment !== originalAutoTaskAssignment;

    // Handle case manager changes as an explicit "on save" behavior (not autosave).
    // Task reassignment is always automatic - all eligible tasks are reassigned.
    if (didCaseManagerChange) {
      if (!nextCaseManagerId && existingCaseManagerIdAtSubmit) {
        // Removing CM: always revert tasks to MP
        await removeCaseManager({
          matterId: data.id,
          data: { revert_tasks_to_mp: true },
        }).unwrap();
      } else if (nextCaseManagerId && !existingCaseManagerIdAtSubmit) {
        // Assigning new CM: tasks are automatically reassigned
        await assignCaseManager({
          matterId: data.id,
          data: {
            case_manager_id: nextCaseManagerId,
            auto_task_assignment: data.auto_task_assignment,
          },
        }).unwrap();
      } else if (nextCaseManagerId && existingCaseManagerIdAtSubmit) {
        // Changing CM: tasks are automatically reassigned
        await changeCaseManager({
          matterId: data.id,
          data: {
            new_case_manager_id: nextCaseManagerId,
            auto_task_assignment: data.auto_task_assignment,
          },
        }).unwrap();
      }
    } else if (
      !didCaseManagerChange &&
      didAutoTaskAssignmentChange &&
      nextCaseManagerId
    ) {
      // CM is unchanged but auto_task_assignment toggle was changed - update config only
      await updateCaseManagerConfig({
        matterId: data.id,
        data: { auto_task_assignment: data.auto_task_assignment ?? true },
      }).unwrap();
    }

    // Services are managed via the Services tab
    // NOTE: firm is intentionally omitted — changing a matter's firm after
    // creation can cause cross-firm data contamination (e.g. firm task
    // templates from the old firm remain on the matter). The firm field
    // is read-only on the update form.
    const patch: any = {
      name: data.name,
      status: data.status,
      matter_type: data.matter_type,
      contact_client_directly: data.contact_client_directly,
      simplified_billing_code: data.simplified_billing_code,
      ...(data.date_of_loss ? { date_of_loss: data.date_of_loss } : {}),
      ...(data.statute_of_limitations_date
        ? { statute_of_limitations_date: data.statute_of_limitations_date }
        : {}),
      ...(data.city ? { city: data.city } : {}),
      ...(data.state ? { state: data.state } : {}),
      ...(data.representing_attorney !== undefined
        ? {
            representing_attorney: extractAttorneyId(
              data.representing_attorney,
            ),
          }
        : {}),
      ...(data.external_cms_url !== undefined
        ? { external_cms_url: data.external_cms_url || null }
        : {}),
      ...(data.external_drive_url !== undefined
        ? { external_drive_url: data.external_drive_url || null }
        : {}),
      ...(data.cms_email !== undefined
        ? { cms_email: data.cms_email || null }
        : {}),
      ...(data.case_id !== undefined
        ? { case_id: data.case_id || null }
        : {}),
      // NOTE: case_manager_idis handled via case manager endpoints above (when changed).
    };

    await patchMatter({
      matter_id: data.id,
      patch,
    }).unwrap();

    toast.success(`Matter "${data.name}" updated successfully`);

    if (onSuccess) {
      onSuccess(data.id);
    } else {
      navigate(`/matters/${data.id}`);
    }
  };

  const onSubmit = async (data: UpdateMatterFormValues) => {
    try {
      // If changing to a terminal status, show modal to require a note first
      if (data.status && isChangingToTerminalStatus(data.status)) {
        setPendingFormData(data);
        setStatusNoteModalOpen(true);
        return;
      }

      // Otherwise, submit directly
      await performSubmit(data);
    } catch (error) {
      captureError(error, { tags: { feature: 'update_matter' } });
      toast.error('Failed to update matter');
    }
  };

  // Handle modal confirmation - create staff note then update status
  const handleStatusNoteConfirm = async (note: string) => {
    if (!pendingFormData) return;

    try {
      // First, create the staff note explaining the status change
      await createStaffNote({
        matterId: pendingFormData.id,
        note: { content: note },
      }).unwrap();

      // Then perform the actual status update
      await performSubmit(pendingFormData);

      // Close modal and reset state
      setStatusNoteModalOpen(false);
      setPendingFormData(null);
    } catch (error) {
      captureError(error, { tags: { feature: 'update_matter' } });
      toast.error('Failed to update matter');
      throw error; // Re-throw to let modal know submission failed
    }
  };

  // Handle modal close - reset pending data
  const handleStatusNoteModalClose = () => {
    setStatusNoteModalOpen(false);
    setPendingFormData(null);
  };

  const handleCancel = () => {
    reset();
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="mx-auto max-w-4xl">
      <h1 className="text-2xl/8 font-semibold text-zinc-950 sm:text-xl/8 dark:text-white">
        Update Matter
      </h1>
      <hr
        role="presentation"
        className="my-10 mt-6 w-full border-t border-zinc-950/10 dark:border-white/10"
      />

      {/* Firm */}
      <FirmSection
        control={control}
        errors={errors}
        selectedFirm={selectedFirm}
        setSelectedFirm={setSelectedFirm}
        setValue={setValue}
        isLoading={isLoading}
        isUpdateMode
      />

      {/* Basics */}
      <BasicsSection
        control={control as any}
        errors={errors}
        isLoading={isLoading}
        hidePhoneNumber
      />

      {/* Client Contact */}
      <ClientContactSection
        control={control as any}
        errors={errors}
        isLoading={isLoading}
      />

      {/* Case Manager - shows if firm has case managers OR matter already has one assigned */}
      {(hasCaseManagers || hasExistingCaseManager) && firmId && (
        <FormSection title="Case Manager" description="Optional">
          <div className="space-y-3">
            <Controller
              name="case_manager_id"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value || 'none'}
                  onValueChange={(value) =>
                    field.onChange(value === 'none' ? null : value)
                  }
                  disabled={
                    isLoading ||
                    isAssigningCaseManager ||
                    isChangingCaseManager ||
                    isRemovingCaseManager ||
                    isUpdatingConfig
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="None (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <span className="text-zinc-500 dark:text-zinc-400">
                        None
                      </span>
                    </SelectItem>
                    {caseManagers.map((cm) => (
                      <SelectItem key={cm.id} value={cm.id}>
                        {cm.display_name}
                      </SelectItem>
                    ))}
                    {/* Show current CM if not in pool so user can see who is assigned */}
                    {hasExistingCaseManager &&
                      !caseManagers.some(
                        (cm) => cm.id === matterData.case_manager?.id,
                      ) &&
                      matterData.case_manager && (
                        <SelectItem
                          value={matterData.case_manager.id}
                          disabled
                        >
                          <span className="text-amber-600 dark:text-amber-400">
                            {matterData.case_manager.display_name} (not in pool)
                          </span>
                        </SelectItem>
                      )}
                  </SelectContent>
                </Select>
              )}
            />

            {/* Auto task assignment toggle - only show when CM is selected */}
            {selectedCaseManagerId && (
              <Controller
                name="auto_task_assignment"
                control={control}
                render={({ field }) => (
                  <CheckboxField>
                    <Checkbox
                      checked={Boolean(field.value)}
                      onChange={(checked) => field.onChange(Boolean(checked))}
                      disabled={isLoading}
                      color="indigo"
                    />
                    <Label className="text-sm cursor-pointer">
                      Auto-assign tasks
                    </Label>
                  </CheckboxField>
                )}
              />
            )}
            {selectedCaseManagerId && (
              <p className="text-xs text-zinc-500 dark:text-zinc-400 ml-7 -mt-1">
                Tasks go to the case manager
              </p>
            )}
          </div>
        </FormSection>
      )}

      {/* Billing */}
      <BillingSection
        selectedBillingCode={selectedBillingCode}
        setValue={setValue}
        isLoading={isLoading}
        firmId={firmId}
      />

      {/* CMS Integration */}
      <CMSIntegrationSection control={control} />

      {/* Additional Details Accordion */}
      <AdditionalDetailsSection
        control={control as any}
        errors={errors}
        isLoading={isLoading}
      />

      {/* Form Actions */}
      <FormActions
        handleCancel={handleCancel}
        isLoading={isLoading}
        isValid={isValid}
        submitLabel="Save Changes"
        isUpdateMode
      />

      {/* Modal for requiring note when changing to terminal status */}
      {pendingFormData && pendingFormData.status && (
        <MatterStatusNoteModal
          isOpen={statusNoteModalOpen}
          onClose={handleStatusNoteModalClose}
          onConfirm={handleStatusNoteConfirm}
          targetStatus={pendingFormData.status}
          matterName={pendingFormData.name}
        />
      )}
    </form>
  );
};
